import type { MagazineCategory } from '~/types/magazine'
import { defineStore } from 'pinia'
import { useNuxtApp } from '#app'
import { baseStore } from '~/stores/Base'
import type {
	LlanaListResponse as ListResponse,
	LlanaWhere as Where,
	LlanaSocketData as SocketData,
	LlanaListOptions,
} from '~/types/llana'

function defaultList(): ListResponse<MagazineCategory> {
	return {
		data: [],
		total: 0,
		limit: default_limit,
		offset: 0,
	}
}

const table_name = 'magazine_categories'
type T = MagazineCategory
const default_sort = 'name.asc'
const default_limit = 999

export class MagazineCategoriesStore extends baseStore<T> {
	constructor() {
		super({
			table_name,
			store_name: table_name,
			default_sort,
			default_limit,
		})
	}
}

export const useMagazineCategoriesStore = () =>
	defineStore(table_name, {
		state: () => <ListResponse<T>>defaultList(),
		actions: {
			async bindEvents(where?: { [key: string]: string | number }) {
				const { $llanaSubscribe } = useNuxtApp()
				await $llanaSubscribe(
					table_name,
					(data: SocketData) => this.get(data.id as number, where),
					(data: SocketData) => this.get(data.id as number, where),
					(data: SocketData) => this.remove(data.id as number, where),
				)
			},
			async create(data: T) {
				const store = new MagazineCategoriesStore()
				const { state, record } = await store.create(this.$state, data)
				this.$state = state
				return record
			},
			async get(id: number, where?: { [key: string]: string | number }): Promise<T | undefined> {
				const store = new MagazineCategoriesStore()
				const { state, record } = await store.get(this.$state, id, where)
				this.$state = state
				return record
			},
			async list(options?: LlanaListOptions): Promise<ListResponse<T>> {
				const store = new MagazineCategoriesStore()
				this.$state = await store.list(this.$state, options)
				return this.$state
			},
			async search(options?: {
				fields?: string[]
				limit?: number
				offset?: number
				where?: Where[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<ListResponse<T>> {
				const store = new MagazineCategoriesStore()
				return await store.search(options)
			},
			async reload(): Promise<ListResponse<T>> {
				const store = new MagazineCategoriesStore()
				this.$state = await store.reload()
				return this.$state
			},
			async update(id: number, data: Partial<T>): Promise<T> {
				const store = new MagazineCategoriesStore()
				const { state, record } = await store.update(this.$state, id, data)
				this.$state = state
				return record
			},
			async remove(id: number, where?: { [key: string]: string | number }): Promise<void> {
				const store = new MagazineCategoriesStore()
				this.$state = await store.remove(this.$state, id, where)
			},
			async delete(id: number): Promise<void> {
				const store = new MagazineCategoriesStore()
				this.$state = await store.delete(this.$state, id)
			},
			async getByHandle(handle: string): Promise<T | undefined> {
				const category = this.$state.data.find((c: T) => c.handle === handle)
				if (category) {
					return category
				}

				const results = await this.search({
					where: [{ field: 'handle', value: handle, operator: 'equals' }],
					limit: 1,
				})

				if (results.data.length > 0) {
					return results.data[0]
				}
				return
			},
			/**
			 * Get the slug for a category by ID, including parent slugs
			 * @param category_id  The category ID to get the slug for
			 * @param slug  The slug to append to the category's slug
			 * @returns
			 */
			getCategorySlug(category_id: number, slug?: string): string {
				let result = ''

				// Find the category in state
				const category = this.$state.data.find((cat: MagazineCategory) => cat.id === category_id)

				// If category not found, return empty string
				if (!category) {
					return result
				}

				// If no parent, return just this category's handle
				if (!category.parent_id) {
					if (slug) {
						result = `${category.handle}/${slug}`
					} else {
						result = category.handle
					}
				} else {
					// Get parent's slug recursively and combine with this category's handle
					if (slug) {
						result = this.getCategorySlug(category.parent_id, `${category.handle}/${slug}`)
					} else {
						result = this.getCategorySlug(category.parent_id, category.handle)
					}
				}

				return result
			},
			/**
			 * Get all children of a category, including children of children
			 * @param category_id  The category ID to get children for
			 * @returns
			 */
			getChildDeepCategories(category_id: number): MagazineCategory[] {
				const children: MagazineCategory[] = []

				if (!children.length) {
					children.push(...this.$state.data.filter((cat: MagazineCategory) => cat.parent_id === category_id))

					for (const child of children) {
						children.push(...this.getChildDeepCategories(child.id))
					}
				}

				//Reduce to unique categories
				return children.filter((cat, index, self) => self.findIndex(c => c.id === cat.id) === index)
			},
		},
	})
